import './noteListRow.scss';

import { EmptyGuid, EntityTypes, StrategyTypes } from '../../utilities/Constants';
import { INoteCategoryModel, INoteModel } from '../../interfaces/INote';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import Authorization from '../../stores/Authorization';
import { CheckmateEasyUpdateIcon } from '../../shared/CheckmateIcons';
import Common from '../../stores/Common';
import { IEntityModel } from '../../interfaces/IAssertionDescriptor';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import NoteAuthHelper from '../../utilities/NoteAuthHelper';
import NoteHelper from '../../utilities/NoteHelper';
import classNames from 'classnames';
import { useAuthContext } from '../../contexts/AuthContext';
import { useZoneContext } from '../../contexts/useZoneContext';

interface INoteListTableRowProps {
    easyUpdateIconClass: string;
    item: INoteModel;
    isEmptyEntity: boolean;
    onAddClick?: (caseGuid: string) => void;
    onEasyUpdateClick: (caseGuid: string, noteGuid: string) => void;
    onShowConfirmRemoveDialog: (note: INoteModel) => void;
    onViewEditNote: (note: INoteModel, readOnly?: boolean) => void;
    readonly?: boolean;
    showDeleteButton?: boolean;
    showEasyUpdateColumn?: boolean;
    showEditButton?: boolean;
    showCaseColumn?: boolean;
    showExpertsColumn?: boolean;
    showTypeColumn?: boolean;
    summaryRowIsExpanded?: boolean;
    isQuadScope?: boolean;
}

export function NoteListTableRow({
    easyUpdateIconClass,
    item,
    isEmptyEntity,
    onAddClick,
    onEasyUpdateClick,
    onShowConfirmRemoveDialog,
    onViewEditNote,
    readonly,
    showCaseColumn,
    showEasyUpdateColumn,
    showExpertsColumn,
    showTypeColumn,
    summaryRowIsExpanded,
    isQuadScope
}: INoteListTableRowProps) {
    const { buildZoneUrl, zoneGuid, buildZoneUrlByOrgGuid } = useZoneContext();
    const { user } = useAuthContext();

    const experts = item.associations?.filter(
        (assoc) => assoc.entityType?.id === EntityTypes.CaseExpert
    );

    const showAdd =
        isEmptyEntity &&
        typeof onAddClick === 'function' &&
        item?.caseGuid &&
        item?.caseGuid !== EmptyGuid;

    const additionalCols =
        (showCaseColumn ? 1 : 0) + (showExpertsColumn ? 1 : 0) + (showTypeColumn ? 1 : 0);

    const handleEasyUpdateClick = (item: INoteModel) => {
        if (typeof onEasyUpdateClick === 'function' && item.caseGuid && item.guid) {
            onEasyUpdateClick(item.caseGuid, item.guid);
        }
    };

    const handleRowAddClick = () => {
        if (showAdd) {
            onAddClick(item.caseGuid ?? '');
        }
    };

     const getCaseFileUrl = (localRoute: string): string => {
        let caseFileUrl = buildZoneUrl(localRoute);
        if (isQuadScope) {
            const zoneGuid = item?.orgGuid ?? '';
            caseFileUrl = buildZoneUrlByOrgGuid(zoneGuid, localRoute);
        }
        return caseFileUrl;
    };

    const showViewButton = NoteAuthHelper.canUserViewNote(item, user, zoneGuid);
    const showEditButton = !readonly && NoteAuthHelper.canUserEditNote(item, user, zoneGuid);
    const showDeleteButton = !readonly && NoteAuthHelper.canUserDeleteNote(item, user, zoneGuid);
    const isLocalBasicUser =
        item.actionTarget && Authorization.isLocalScope(item.actionTarget, zoneGuid);

    return (
        <>
            <tr key={item.guid} className={classNames({ 'no-border-bottom': !isEmptyEntity })}>
                <td style={{ textAlign: 'left' }}>
                    <StarStatusIcon note={item} />
                </td>
                {showCaseColumn && (
                    <td>
                        {item.caseGuid && (
                            <Link
                                target="_blank"
                                to={getCaseFileUrl(
                                    LocalRoutes.CaseFile.replace(':guid', item.caseGuid)
                                )}
                            >
                                {item.caseName}
                            </Link>
                        )}
                    </td>
                )}
                <td>{item.orgName}</td>
                {showTypeColumn && <td>{item.type && <>{item.originStrategyTypeName}</>}</td>}
                {showTypeColumn && (
                    <td>
                        {item.type && (
                            <StrategyDetailLink
                                type={item.originStrategyType ?? 0}
                                name={item.originEntityName ?? ''}
                                guid={item.originEntityKey ?? ''}
                                buildZoneUrl={buildZoneUrl}
                            />
                        )}
                    </td>
                )}
                <td>
                    {item.categories?.map((value: INoteCategoryModel, i: number) => {
                        return <div key={i}>{value.fullText}</div>;
                    })}
                </td>
                <td>
                    {item.private && <i className="fal fa-lg fa-location-dot-slash color-gray" />}
                </td>
                <td>
                    {item.allowLocalEdit || isLocalBasicUser ? (
                        <i
                            className={
                                'fal fa-lg fa-map-marker-edit ' +
                                (isLocalBasicUser ? 'color-green' : 'color-gray')
                            }
                        />
                    ) : null}
                </td>
                <td>
                    {item.modifiedByLocal && (
                        <i className="fal fa-lg fa-map-marker-smile color-gray" />
                    )}
                </td>
                <td>
                    {item.context && item.context.trim().length > 0 && (
                        <i className="fal fa-lg fa-message-dots color-gray" />
                    )}
                </td>
                <td>{item.hasAttachment && <i className="fal fa-lg fa-paperclip color-gray" />}</td>
                {showExpertsColumn && (
                    <td>{experts?.some((e) => e) && <ExpertsTooltip experts={experts} />}</td>
                )}
                <td className="col">{item.purpose?.displayName}</td>
                <td className="col">{NoteHelper.getNoteDecisionText(item)}</td>
                <td className="col">{NoteHelper.getModifiedByName(item.author)}</td>
                <td className="col">
                    {!isEmptyEntity && Common.dateTimeFormatToLocal(item.modifiedDate)}
                </td>
                <td className="table-actions">
                    <div
                        className="text-nowrap"
                        style={{ display: 'flex', gap: 0, justifyContent: 'flex-end' }}
                    >
                        {!isEmptyEntity && (
                            <>
                                {showViewButton && (
                                    <button
                                        className="btn btn-no-bg"
                                        onClick={() => {
                                            onViewEditNote(item, true);
                                        }}
                                    >
                                        <i className="fal fa-lg fa-eye" />
                                    </button>
                                )}
                                {showEditButton && (
                                    <button
                                        className="btn btn-no-bg"
                                        onClick={() => {
                                            onViewEditNote(item);
                                        }}
                                    >
                                        <i className="fal fa-lg fa-pen" />
                                    </button>
                                )}
                                {showDeleteButton && (
                                    <button
                                        className="btn btn-no-bg"
                                        onClick={() => {
                                            onShowConfirmRemoveDialog(item);
                                        }}
                                    >
                                        <i className="fal fa-lg fa-trash-alt" />
                                    </button>
                                )}
                            </>
                        )}

                        {showAdd && (
                            <button
                                className="btn btn-no-bg"
                                style={{ margin: 0 }}
                                onClick={handleRowAddClick}
                            >
                                <i className="fal fa-plus" />
                            </button>
                        )}

                        {showEasyUpdateColumn && (
                            <button
                                className="btn btn-no-bg float-end"
                                onClick={() => handleEasyUpdateClick(item)}
                            >
                                <CheckmateEasyUpdateIcon
                                    size="lg"
                                    className={easyUpdateIconClass}
                                />
                            </button>
                        )}
                    </div>
                </td>
            </tr>
            {(item.content?.trim().length ?? 0) > 0 && (
                <SummaryRow
                    colSpan={11 + additionalCols}
                    content={item.content ?? ''}
                    isExpanded={!!summaryRowIsExpanded}
                />
            )}
        </>
    );
}

function ExpertsTooltip({ experts }: { experts: IEntityModel[] }) {
    const renderTooltip = (props: any) => (
        <Tooltip id="button-tooltip" {...props}>
            <div style={{ textAlign: 'left' }}>
                {experts.map((expert) => (
                    <div key={expert.entityGuid}>{expert.displayName}</div>
                ))}
            </div>
        </Tooltip>
    );

    return (
        <OverlayTrigger placement="right" delay={{ show: 250, hide: 400 }} overlay={renderTooltip}>
            <i className="fal fa-lg fa-head-side-brain color-gray" />
        </OverlayTrigger>
    );
}

interface ISummaryRowProps {
    colSpan: number;
    content: string;
    isExpanded: boolean;
}

function SummaryRow(props: ISummaryRowProps) {
    const [localIsExpanded, setLocalIsExpanded] = useState<boolean>(props.isExpanded);

    const handleExpandedClick = () => {
        setLocalIsExpanded((prev) => !prev);
    };

    useEffect(() => {
        setLocalIsExpanded(props.isExpanded);
    }, [props.isExpanded]);

    return (
        <tr>
            <td colSpan={props.colSpan} style={{ padding: '0 1em 0 0' }}>
                <div>
                    <button
                        className="btn btn-no-bg"
                        onClick={handleExpandedClick}
                        style={{ marginLeft: -12 }}
                    >
                        <div
                            className="text-gray"
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '15px',
                                flexDirection: 'row',
                                marginBottom: '3px',
                            }}
                        >
                            <i
                                className={classNames('fa-regular', {
                                    'fa-minus': localIsExpanded,
                                    'fa-plus': !localIsExpanded,
                                })}
                            />
                            <span>Summary</span>
                        </div>
                    </button>
                    <div
                        style={{
                            marginLeft: '28px',
                            visibility: localIsExpanded ? 'visible' : 'collapse',
                            height: localIsExpanded ? 'auto' : 0,
                        }}
                    >
                        <div
                            style={{
                                paddingBottom: '1em',
                            }}
                        >
                            {props.content?.split('\n').map((item, key) => {
                                return (
                                    <span key={key}>
                                        {item.trim()}
                                        <br />
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </td>
            <td />
        </tr>
    );
}

interface IStrategyDetailLinkProps {
    type: number;
    name: string;
    guid: string;
    buildZoneUrl: (path: string) => string;
}

function StrategyDetailLink(props: IStrategyDetailLinkProps) {
    const route = (type: number, guid: string): string => {
        switch (type) {
            case StrategyTypes.Jurisdiction.Value:
                return LocalRoutes.JurisdictionStrategyDetail.replace(':guid', guid);
            case StrategyTypes.PlaintiffsFirm.Value:
                return LocalRoutes.PlaintiffFirmStrategyDetail.replace(':guid', guid);
            case StrategyTypes.LocalCounsel.Value:
                return LocalRoutes.LocalCounselStrategyDetail.replace(':guid', guid);
            case StrategyTypes.Projects.Value:
                return LocalRoutes.Projects.replace(':guid', guid);
            case StrategyTypes.Experts.Value:
                return LocalRoutes.ExpertDetail.replace(':id', guid);
            default:
                break;
        }

        return '';
    };

    return (
        <Link target="_blank" to={props.buildZoneUrl(route(props.type, props.guid))}>
            {props.name}
        </Link>
    );
}

function StarStatusIcon(props: { note: INoteModel }) {
    return (
        <span
            className={classNames('star', props.note.starred ? 'starred' : '')}
            title={props.note.starred ? 'Starred' : 'Not Starred'}
        >
            <i
                className={classNames(
                    'fa-lg',
                    props.note.starred ? 'fa fa-star-sharp color-yellow' : 'fal fa-star-sharp'
                )}
            />
        </span>
    );
}
